import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Subject, takeUntil } from 'rxjs';
import { StaffCommissionService } from '../../services/staff-commission.service';
import {
  StaffCommissionResponse,
  CommissionTransaction,
  StaffCommissionStatistics,
} from '../../models/staff-commission.model';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';

@Component({
  selector: 'lib-my-commission',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    TableModule,
    CardModule,
    ProgressSpinnerModule,
    MessageModule,
    TagModule,
    ButtonModule,
    SelectModule,
    DatePipe,
    CurrencyformatPipe,
    DateTimeFormatPipe,
    DatePipe
  ],
  templateUrl: './my-commission.component.html',
  styleUrl: './my-commission.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyCommissionComponent implements OnInit, OnDestroy {
  private staffCommissionService = inject(StaffCommissionService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();

  loading = false;
  error: string | null = null;
  statistics: StaffCommissionStatistics | null = null;
  transactions: CommissionTransaction[] = [];
  expandedRows: { [key: number]: boolean } = {};

  // Month selection
  months: { label: string; value: string }[] = [];
  selectedMonth: string = '';

  private datePipe: DatePipe;

  constructor() {
    this.datePipe = new DatePipe('en-US');
  }

  ngOnInit(): void {
    this.generateMonthOptions();
    this.loadCommissionData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  generateMonthOptions(): void {
    const months: { label: string; value: string }[] = [];
    const currentDate = new Date();

    // Generate 1 future month + current month + 12 past months (14 total)
    for (let i = -1; i <= 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const label = this.datePipe.transform(date, 'MMMM yyyy') || '';
      const value = date.toISOString();
      months.push({ label, value });
    }

    this.months = months;
    // Set current month as selected (index 1, since 1 future month comes first)
    this.selectedMonth = months[1].value;
  }

  onMonthChange(): void {
    this.loadCommissionData();
  }

  loadCommissionData(): void {
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    // selectedMonth is already an ISO string representing the first day of the month
    const periodDate = this.selectedMonth;

    this.staffCommissionService
      .getCurrentPeriodCommissions(periodDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: StaffCommissionResponse) => {
          this.statistics = response.statistics;
          this.transactions = response.transactions;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading commission data:', error);
          this.error = 'Failed to load commission data. Please try again.';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getTransactionType(transaction: CommissionTransaction): string {
    return transaction.commission_transaction_type;
  }

  getTransactionTypeIcon(transaction: CommissionTransaction): string {
    if (transaction.appointment) return 'fa fa-calendar-check';
    if (transaction.class) return 'fa fa-users';
    if (transaction.subscription) return 'fa fa-repeat';
    if (transaction.invoice) return 'fa fa-file-invoice';
    if (transaction.payment) return 'fa fa-credit-card';
    return 'fa fa-question';
  }

  getTransactionModeLabel(mode: string): string {
    const modeKey = mode?.toLowerCase().replace(/_/g, '_').toUpperCase();
    return `STAFF.STAFF_PREFERENCE.COMMISSION.TRANSACTION_MODE_${modeKey}`;
  }

  expandAll(): void {
    this.expandedRows = {};
    this.transactions.forEach(transaction => {
      this.expandedRows[transaction.id] = true;
    });
    this.cdr.markForCheck();
  }

  collapseAll(): void {
    this.expandedRows = {};
    this.cdr.markForCheck();
  }

  onRowExpand(event: any): void {
    // Handle row expansion if needed
    console.log('Row expanded:', event.data);
  }

  onRowCollapse(event: any): void {
    // Handle row collapse if needed
    console.log('Row collapsed:', event.data);
  }
}

